import { createContext, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import type { QueryKey } from '@tanstack/react-query';

import { ensureDefined } from '@e-socna/utils';
import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { useGraphQLMutationForFileUpload } from '../../hooks/useGraphQLMutation.hook';
import { useQueue } from '../../hooks/useQueue.hook';
import { environment } from '../../environment';

import { useReferencesContext } from '../ReferencesContext/hooks/useReferencesContext.hook';
import { BackgroundOperationType } from '../UiContext/enums/background-operation-type.enum';
import { useUiContext } from '../UiContext/hooks/useUiContext.hook';
import type { BackgroundOperationKey } from '../UiContext/types/background-operation-key.type';

import type { Compressor } from './types/compressor.type';

export const FileUploaderContextProvider_File_Fragment = graphql(`
  fragment FileUploaderContextProvider_File_Fragment on File {
    uri
  }
`);

export type FileUploaderContextProvider_File = ResultOf<
  typeof FileUploaderContextProvider_File_Fragment
>;

const FileUploaderContextProvider_UploadBusinessEventFile_Mutation = graphql(`
  mutation FileUploaderContextProvider_UploadBusinessEventFile(
    $file: Upload!
    $fileCategoryId: Id!
    $entityId: Id!
  ) {
    upload: uploadBusinessEventFile(
      file: $file
      fileCategoryId: $fileCategoryId
      entityId: $entityId
    ) {
      ...FileUploaderContextProvider_File_Fragment
    }
  }
`);

const FileUploaderContextProvider_UploadCostFile_Mutation = graphql(`
  mutation FileUploaderContextProvider_UploadCostFile(
    $file: Upload!
    $fileCategoryId: Id!
    $entityId: Id!
  ) {
    upload: uploadCostFile(
      file: $file
      fileCategoryId: $fileCategoryId
      entityId: $entityId
    ) {
      ...FileUploaderContextProvider_File_Fragment
    }
  }
`);

const FileUploaderContextProvider_UploadDocumentTemplateFile_Mutation = graphql(
  `
    mutation FileUploaderContextProvider_UploadDocumentTemplateFile(
      $file: Upload!
      $fileCategoryId: Id!
      $entityId: Id!
    ) {
      upload: uploadDocumentTemplateFile(
        file: $file
        fileCategoryId: $fileCategoryId
        entityId: $entityId
      ) {
        ...FileUploaderContextProvider_File_Fragment
      }
    }
  `,
);

const FileUploaderContextProvider_UploadFolderFile_Mutation = graphql(`
  mutation FileUploaderContextProvider_UploadFolderFile(
    $file: Upload!
    $fileCategoryId: Id!
    $entityId: Id!
  ) {
    upload: uploadFolderFile(
      file: $file
      fileCategoryId: $fileCategoryId
      entityId: $entityId
    ) {
      ...FileUploaderContextProvider_File_Fragment
    }
  }
`);

const FileUploaderContextProvider_UploadMaterialFile_Mutation = graphql(`
  mutation FileUploaderContextProvider_UploadMaterialFile(
    $file: Upload!
    $fileCategoryId: Id!
    $entityId: Id!
  ) {
    upload: uploadMaterialFile(
      file: $file
      fileCategoryId: $fileCategoryId
      entityId: $entityId
    ) {
      ...FileUploaderContextProvider_File_Fragment
    }
  }
`);

const FileUploaderContextProvider_UploadMissionFile_Mutation = graphql(`
  mutation FileUploaderContextProvider_UploadMissionFile(
    $file: Upload!
    $fileCategoryId: Id!
    $entityId: Id!
  ) {
    upload: uploadMissionFile(
      file: $file
      fileCategoryId: $fileCategoryId
      entityId: $entityId
    ) {
      ...FileUploaderContextProvider_File_Fragment
    }
  }
`);

const FileUploaderContextProvider_UploadResolvableMissionTypeFile_Mutation =
  graphql(`
    mutation FileUploaderContextProvider_UploadResolvableMissionTypeFile(
      $file: Upload!
      $fileCategoryId: Id!
      $entityId: Id!
    ) {
      upload: uploadResolvableMissionTypeFile(
        file: $file
        fileCategoryId: $fileCategoryId
        entityId: $entityId
      ) {
        ...FileUploaderContextProvider_File_Fragment
      }
    }
  `);

const FileUploaderContextProvider_UploadQuotationFile_Mutation = graphql(`
  mutation FileUploaderContextProvider_UploadQuotationFile(
    $file: Upload!
    $fileCategoryId: Id!
    $entityId: Id!
  ) {
    upload: uploadQuotationFile(
      file: $file
      fileCategoryId: $fileCategoryId
      entityId: $entityId
    ) {
      ...FileUploaderContextProvider_File_Fragment
    }
  }
`);

const FileUploaderContextProvider_UploadReportFile_Mutation = graphql(`
  mutation FileUploaderContextProvider_UploadReportFile(
    $file: Upload!
    $fileCategoryId: Id!
    $entityId: Id!
  ) {
    upload: uploadReportFile(
      file: $file
      fileCategoryId: $fileCategoryId
      entityId: $entityId
    ) {
      ...FileUploaderContextProvider_File_Fragment
    }
  }
`);

const FileUploaderContextProvider_UploadSectionTemplateFile_Mutation = graphql(`
  mutation FileUploaderContextProvider_UploadSectionTemplateFile(
    $file: Upload!
    $fileCategoryId: Id!
    $entityId: Id!
  ) {
    upload: uploadSectionTemplateFile(
      file: $file
      fileCategoryId: $fileCategoryId
      entityId: $entityId
    ) {
      ...FileUploaderContextProvider_File_Fragment
    }
  }
`);

const FileUploaderContextProvider_UploadResolvableServiceFile_Mutation =
  graphql(`
    mutation FileUploaderContextProvider_UploadResolvableServiceFile(
      $file: Upload!
      $fileCategoryId: Id!
      $entityId: Id!
    ) {
      upload: uploadResolvableServiceFile(
        file: $file
        fileCategoryId: $fileCategoryId
        entityId: $entityId
      ) {
        ...FileUploaderContextProvider_File_Fragment
      }
    }
  `);

const FileUploaderContextProvider_UploadThirdPartyFile_Mutation = graphql(`
  mutation FileUploaderContextProvider_UploadThirdPartyFile(
    $file: Upload!
    $fileCategoryId: Id!
    $entityId: Id!
  ) {
    upload: uploadThirdPartyFile(
      file: $file
      fileCategoryId: $fileCategoryId
      entityId: $entityId
    ) {
      ...FileUploaderContextProvider_File_Fragment
    }
  }
`);

const FileUploaderContextProvider_UploadUserFile_Mutation = graphql(`
  mutation FileUploaderContextProvider_UploadUserFile(
    $file: Upload!
    $fileCategoryId: Id!
    $entityId: Id!
  ) {
    upload: uploadUserFile(
      file: $file
      fileCategoryId: $fileCategoryId
      entityId: $entityId
    ) {
      ...FileUploaderContextProvider_File_Fragment
    }
  }
`);

type FileUploaderContextProviderMutations =
  | typeof FileUploaderContextProvider_UploadBusinessEventFile_Mutation
  | typeof FileUploaderContextProvider_UploadCostFile_Mutation
  | typeof FileUploaderContextProvider_UploadDocumentTemplateFile_Mutation
  | typeof FileUploaderContextProvider_UploadFolderFile_Mutation
  | typeof FileUploaderContextProvider_UploadMaterialFile_Mutation
  | typeof FileUploaderContextProvider_UploadMissionFile_Mutation
  | typeof FileUploaderContextProvider_UploadResolvableMissionTypeFile_Mutation
  | typeof FileUploaderContextProvider_UploadQuotationFile_Mutation
  | typeof FileUploaderContextProvider_UploadReportFile_Mutation
  | typeof FileUploaderContextProvider_UploadSectionTemplateFile_Mutation
  | typeof FileUploaderContextProvider_UploadResolvableServiceFile_Mutation
  | typeof FileUploaderContextProvider_UploadThirdPartyFile_Mutation
  | typeof FileUploaderContextProvider_UploadUserFile_Mutation;

type FileUploadFnParams = VariablesOf<FileUploaderContextProviderMutations> &
  Readonly<{
    compressor?: Compressor;
    fileDirectoryCategoryId: Id;
    handleError?: () => void;
    handleSuccess?: (
      mutationResultData: Readonly<FileUploaderContextProvider_File>,
    ) => void;
    queryKeysToInvalidate: readonly QueryKey[];
  }>;

interface FileUploadFnResult {
  readonly fileUrl: string;
}

type FileUploadFn = (
  params: Readonly<FileUploadFnParams>,
) => Promise<FileUploadFnResult>;

interface FileUploaderData {
  readonly fileUpload: FileUploadFn;
}

const FileUploaderContext = createContext<FileUploaderData>({
  fileUpload: () => Promise.resolve({ fileUrl: EMPTY_STRING }),
});

const UPLOAD_BACKGROUND_OPERATION_KEY: BackgroundOperationKey = {
  type: BackgroundOperationType.Upload,
};

interface Props {
  readonly children: ReactNode;
}

const FileUploaderContextProvider = ({ children }: Readonly<Props>) => {
  const queryClient = useQueryClient();

  const { fileDirectoryCategoriesCodeMap } = useReferencesContext();

  const { add, size, first, remove } = useQueue<
    FileUploadFnParams &
      Readonly<{
        resolve: (value: FileUploadFnResult) => void;
        reject: (reason?: unknown) => void;
      }>
  >();

  const { mutateAsync: uploadBusinessEventFile } =
    useGraphQLMutationForFileUpload(
      FileUploaderContextProvider_UploadBusinessEventFile_Mutation,
    );

  const { mutateAsync: uploadCostFile } = useGraphQLMutationForFileUpload(
    FileUploaderContextProvider_UploadCostFile_Mutation,
  );

  const { mutateAsync: uploadDocumentTemplateFile } =
    useGraphQLMutationForFileUpload(
      FileUploaderContextProvider_UploadDocumentTemplateFile_Mutation,
    );

  const { mutateAsync: uploadFolderFile } = useGraphQLMutationForFileUpload(
    FileUploaderContextProvider_UploadFolderFile_Mutation,
  );

  const { mutateAsync: uploadMaterialFile } = useGraphQLMutationForFileUpload(
    FileUploaderContextProvider_UploadMaterialFile_Mutation,
  );

  const { mutateAsync: uploadMissionFile } = useGraphQLMutationForFileUpload(
    FileUploaderContextProvider_UploadMissionFile_Mutation,
  );

  const { mutateAsync: uploadResolvableMissionTypeFile } =
    useGraphQLMutationForFileUpload(
      FileUploaderContextProvider_UploadResolvableMissionTypeFile_Mutation,
    );

  const { mutateAsync: uploadQuotationFile } = useGraphQLMutationForFileUpload(
    FileUploaderContextProvider_UploadQuotationFile_Mutation,
  );

  const { mutateAsync: uploadReportFile } = useGraphQLMutationForFileUpload(
    FileUploaderContextProvider_UploadReportFile_Mutation,
  );

  const { mutateAsync: uploadSectionTemplateFile } =
    useGraphQLMutationForFileUpload(
      FileUploaderContextProvider_UploadSectionTemplateFile_Mutation,
    );

  const { mutateAsync: uploadResolvableServiceFile } =
    useGraphQLMutationForFileUpload(
      FileUploaderContextProvider_UploadResolvableServiceFile_Mutation,
    );

  const { mutateAsync: uploadThirdPartyFile } = useGraphQLMutationForFileUpload(
    FileUploaderContextProvider_UploadThirdPartyFile_Mutation,
  );

  const { mutateAsync: uploadUserFile } = useGraphQLMutationForFileUpload(
    FileUploaderContextProvider_UploadUserFile_Mutation,
  );

  const value = useMemo(() => {
    const fileUpload = ({
      compressor,
      file,
      fileCategoryId,
      fileDirectoryCategoryId,
      entityId,
      handleError,
      handleSuccess,
      queryKeysToInvalidate,
    }: Readonly<FileUploadFnParams>) =>
      new Promise<FileUploadFnResult>((resolve, reject) => {
        add({
          compressor,
          file,
          fileCategoryId,
          fileDirectoryCategoryId,
          entityId,
          handleError,
          handleSuccess,
          queryKeysToInvalidate,
          reject,
          resolve,
        });
      });

    return { fileUpload };
  }, [add]);

  const { startBackgroundOperation, endBackgroundOperation } = useUiContext();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (size) {
      startBackgroundOperation(UPLOAD_BACKGROUND_OPERATION_KEY);
    } else {
      endBackgroundOperation(UPLOAD_BACKGROUND_OPERATION_KEY);
    }

    return () => {
      if (size) {
        endBackgroundOperation(UPLOAD_BACKGROUND_OPERATION_KEY);
      }
    };
  }, [size, startBackgroundOperation, endBackgroundOperation]);

  useEffect(() => {
    if (!first) {
      return;
    }

    const {
      compressor,
      file,
      fileCategoryId,
      fileDirectoryCategoryId,
      entityId,
      handleError,
      handleSuccess,
      queryKeysToInvalidate,
      reject,
      resolve,
    } = first;

    const fileUploaderMutationMap = {
      [fileDirectoryCategoriesCodeMap.BusinessEvent._id]: {
        fieldName: 'uploadBusinessEventFile',
        uploadFn: uploadBusinessEventFile,
      },
      [fileDirectoryCategoriesCodeMap.Cost._id]: {
        fieldName: 'uploadCostFile',
        uploadFn: uploadCostFile,
      },
      [fileDirectoryCategoriesCodeMap.DocumentTemplate._id]: {
        fieldName: 'uploadDocumentTemplateFile',
        uploadFn: uploadDocumentTemplateFile,
      },
      [fileDirectoryCategoriesCodeMap.Folder._id]: {
        fieldName: 'uploadFolderFile',
        uploadFn: uploadFolderFile,
      },
      [fileDirectoryCategoriesCodeMap.Material._id]: {
        fieldName: 'uploadMaterialFile',
        uploadFn: uploadMaterialFile,
      },
      [fileDirectoryCategoriesCodeMap.Mission._id]: {
        fieldName: 'uploadMissionFile',
        uploadFn: uploadMissionFile,
      },
      [fileDirectoryCategoriesCodeMap.ResolvableMissionType._id]: {
        fieldName: 'uploadResolvableMissionTypeFile',
        uploadFn: uploadResolvableMissionTypeFile,
      },
      [fileDirectoryCategoriesCodeMap.Quotation._id]: {
        fieldName: 'uploadQuotationFile',
        uploadFn: uploadQuotationFile,
      },
      [fileDirectoryCategoriesCodeMap.Report._id]: {
        fieldName: 'uploadReportFile',
        uploadFn: uploadReportFile,
      },
      [fileDirectoryCategoriesCodeMap.SectionTemplate._id]: {
        fieldName: 'uploadSectionTemplateFile',
        uploadFn: uploadSectionTemplateFile,
      },
      [fileDirectoryCategoriesCodeMap.ResolvableService._id]: {
        fieldName: 'uploadResolvableServiceFile',
        uploadFn: uploadResolvableServiceFile,
      },
      [fileDirectoryCategoriesCodeMap.ThirdParty._id]: {
        fieldName: 'uploadThirdPartyFile',
        uploadFn: uploadThirdPartyFile,
      },
      [fileDirectoryCategoriesCodeMap.User._id]: {
        fieldName: 'uploadUserFile',
        uploadFn: uploadUserFile,
      },
    } as const;

    const getCompressedFile = async (file: File) =>
      compressor ? compressor.compress(file) : file;

    const {
      fileUploader: { uploadFn },
    } = ensureDefined({
      fileUploader: fileUploaderMutationMap[fileDirectoryCategoryId],
    });

    getCompressedFile(file)
      .then((compressedFile) => {
        uploadFn({
          file: compressedFile,
          fileCategoryId,
          entityId,
        })
          .then((data) => {
            const result = getFragmentData(
              FileUploaderContextProvider_File_Fragment,
              data.upload,
            );

            resolve({
              fileUrl: `${environment.backendUrl}/${result.uri}?compressed=true`,
            });

            queryKeysToInvalidate.forEach((queryKey) => {
              void queryClient.invalidateQueries({ queryKey });
            });

            handleSuccess?.(result);

            remove();
          })
          .catch((error: unknown) => {
            reject(error);
            remove();
            enqueueSnackbar("Erreur lors de l'envoi du fichier.", {
              variant: 'error',
            });
            handleError?.();
            console.error(error);
          });
      })
      .catch((error: unknown) => {
        reject(error);
        remove();
        enqueueSnackbar('Erreur lors de la compression du fichier.', {
          variant: 'error',
        });
        handleError?.();
        console.error(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first]);

  return (
    <FileUploaderContext.Provider value={value}>
      {children}
    </FileUploaderContext.Provider>
  );
};

export { FileUploaderContext, FileUploaderContextProvider };
